import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Check } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { submitLead, whatsappLink } from "../lib/api";

const services = ["Content Creation", "Social Media", "Website Design", "Google My Business", "Everything"];

export default function LeadForm() {
  const [form, setForm] = useState({ name: "", email: "", practice: "", specialty: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    const handler = (e) => {
      const { service, biz } = e.detail || {};
      setDone(false);
      setForm((f) => ({ ...f, service: service || f.service, practice: biz || f.practice }));
    };
    window.addEventListener("contentlab:prefill", handler);
    return () => window.removeEventListener("contentlab:prefill", handler);
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required.");
      return;
    }
    setLoading(true);
    try {
      await submitLead(form);
      const msg =
        `Hi Content Lab! I'd like to get in touch.\n\n` +
        `• Name: ${form.name}\n` +
        `• Email: ${form.email}\n` +
        (form.practice ? `• Practice: ${form.practice}\n` : "") +
        (form.specialty ? `• Specialty: ${form.specialty}\n` : "") +
        (form.service ? `• Interested in: ${form.service}\n` : "") +
        (form.message ? `• Message: ${form.message}\n` : "");
      window.open(whatsappLink(msg), "_blank", "noopener,noreferrer");
      setDone(true);
      toast.success("Opening WhatsApp — we'll continue the chat there!");
      setForm({ name: "", email: "", practice: "", specialty: "", service: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="audit" className="bg-slate-50 py-16 md:py-20">
      <div className="mx-auto max-w-6xl px-5 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7 }}
          className="grid grid-cols-1 overflow-hidden rounded-[2.5rem] border border-slate-200 bg-white shadow-[0_30px_80px_-40px_rgba(3,105,161,0.35)] lg:grid-cols-2"
        >
          {/* Left pitch */}
          <div className="relative flex flex-col justify-between overflow-hidden bg-obsidian p-8 text-white md:p-12">
            <div className="noise-overlay absolute inset-0 opacity-[0.05]" />
            <div className="absolute -bottom-16 -right-16 h-56 w-56 rounded-full bg-blue-brand/20 blur-3xl" />
            <div className="relative">
              <span className="font-mono text-xs uppercase tracking-[0.25em] text-lab-green">/ Free growth audit</span>
              <h2 className="mt-5 font-display text-4xl font-extrabold leading-[1.02] tracking-tight md:text-5xl">
                Let&apos;s diagnose your online presence.
              </h2>
              <p className="mt-5 max-w-md text-slate-400">
                Tell us about your practice. We&apos;ll send back a no-fluff audit of your social,
                website &amp; Google presence — and exactly what to fix first. Zero cost, zero pressure.
              </p>
            </div>
            <ul className="relative mt-10 space-y-3">
              {["Honest scorecard of your channels", "3 quick wins you can action today", "A tailored 90-day growth plan"].map((li) => (
                <li key={li} className="flex items-center gap-3 text-sm text-slate-300">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-lab-green"><Check className="h-3 w-3 text-obsidian" /></span>
                  {li}
                </li>
              ))}
            </ul>
          </div>

          {/* Right form */}
          <div className="p-8 md:p-12">
            {done ? (
              <div data-testid="lead-success" className="flex h-full flex-col items-center justify-center text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-lab-light">
                  <Check className="h-8 w-8 text-lab-deep" />
                </div>
                <h3 className="mt-6 font-display text-2xl font-bold text-obsidian">You&apos;re on the list.</h3>
                <p className="mt-2 max-w-xs text-slate-600">Our team will review your practice and reach out within 24 hours.</p>
                <button
                  data-testid="lead-reset"
                  onClick={() => setDone(false)}
                  className="mt-6 rounded-full border border-slate-300 px-5 py-2.5 font-mono text-xs uppercase tracking-[0.15em] text-obsidian"
                >
                  Submit another
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-5" data-testid="lead-form">
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="font-mono text-xs uppercase tracking-[0.1em] text-slate-500">Full name *</Label>
                    <Input id="name" data-testid="lead-name" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Dr. Jane Doe" className="rounded-xl" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="font-mono text-xs uppercase tracking-[0.1em] text-slate-500">Email *</Label>
                    <Input id="email" type="email" data-testid="lead-email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="jane@clinic.com" className="rounded-xl" />
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="practice" className="font-mono text-xs uppercase tracking-[0.1em] text-slate-500">Practice / brand</Label>
                    <Input id="practice" data-testid="lead-practice" value={form.practice} onChange={(e) => update("practice", e.target.value)} placeholder="Bloom Dental" className="rounded-xl" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="specialty" className="font-mono text-xs uppercase tracking-[0.1em] text-slate-500">Specialty</Label>
                    <Input id="specialty" data-testid="lead-specialty" value={form.specialty} onChange={(e) => update("specialty", e.target.value)} placeholder="Dermatology" className="rounded-xl" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="font-mono text-xs uppercase tracking-[0.1em] text-slate-500">What do you need most?</Label>
                  <Select value={form.service} onValueChange={(v) => update("service", v)}>
                    <SelectTrigger data-testid="lead-service" className="rounded-xl">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map((s) => <SelectItem key={s} value={s} data-testid={`service-opt-${s}`}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message" className="font-mono text-xs uppercase tracking-[0.1em] text-slate-500">Anything else?</Label>
                  <Textarea id="message" data-testid="lead-message" value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Tell us about your goals..." className="min-h-[96px] rounded-xl" />
                </div>
                <button
                  type="submit"
                  data-testid="lead-submit"
                  disabled={loading}
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-blue-brand px-6 py-4 font-mono text-xs uppercase tracking-[0.15em] text-white transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
                >
                  {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending</> : "Submit"}
                </button>
              </form>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
